import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Input } from 'semantic-ui-react'

import { IMAGE_SIZES, Shield, Trophy } from 'images'
import { actions, selectors } from 'teams'
import { Card, ProgressBar, SimpleButton, Tooltip } from 'ui'

import type { FC, MouseEventHandler, ReactNode } from 'react'
import type { TeamSchema } from 'teams'

const ITEMS_PER_PAGE = 10

export interface SearchTeamProps extends Required<Pick<TeamSchema, 'category' | 'discipline' | 'gender'>> {
  onClose: () => void
  onSelect: (team: TeamSchema) => void

  allowCreation?: boolean
  children?: ReactNode
}

const SearchTeam: FC<SearchTeamProps> = ({
  allowCreation = false,
  category,
  children,
  discipline,
  gender,
  onClose,
  onSelect,
}) => {
  const dispatch = useDispatch()
  const { key, data, loading, pagination } = useSelector(selectors.list)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const uniqueKey = `${category.id}${discipline.id}${gender.id}`

  useEffect(() => {
    if (key && key !== uniqueKey) {
      dispatch(actions.teamsListClear())
    }
    // clean on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (search.trim().length >= 3) {
      const filters: Parameters<typeof actions.teamsList>[0] = {
        key: uniqueKey,
        categoryId: category.id,
        disciplineId: discipline.id,
        genderId: gender.id,
        page,
        per_page: ITEMS_PER_PAGE,
        search,
      }
      dispatch(actions.teamsList(filters))
    }
  }, [category.id, discipline.id, dispatch, gender.id, page, search, uniqueKey])

  const onSelectTeam: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    event.preventDefault()
    const team = data.find((item) => item.id === event.currentTarget.value)
    if (team) {
      onSelect(team)
    }
  }

  const onCreateTeam: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    event.preventDefault()
    dispatch(
      actions.teamCreate({
        categoryId: category.id,
        disciplineId: discipline.id,
        genderId: gender.id,
        name: search,
        nickname: search.substring(0, 10),
      }),
    )

    const filters: Parameters<typeof actions.teamsList>[0] = {
      key: uniqueKey,
      categoryId: category.id,
      disciplineId: discipline.id,
      genderId: gender.id,
      page,
      per_page: ITEMS_PER_PAGE,
      search,
    }
    dispatch(actions.teamsList(filters))
  }

  return (
    <div className="flex flex-col gap-2">
      {children && <div>{children}</div>}
      <div className="flex flex-wrap gap-2 items-center">
        <b>Buscar equipos:</b>
        <span className="border rounded py-1 px-2 text-sm">
          <b>Categoría:</b> {category.name}
        </span>
        <span className="border rounded py-1 px-2 text-sm">
          <b>Disciplina:</b> {discipline.name}
        </span>
        <span className="border rounded py-1 px-2 text-sm">
          <b>Género:</b> {gender.name}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input label="Buscar" onChange={(_event, { value }) => setSearch(value)} value={search} />
        <SimpleButton icon="cancel" onClick={onClose}>
          Cerrar
        </SimpleButton>
      </div>
      {loading && (
        <div className="text-sm border-t bg-greyLightest">
          <div className="px-2">Buscando equipos...</div>
          <ProgressBar value={100} />
        </div>
      )}
      <div className="m-0 p-2 border-t bg-greyLightest grid grid-cols-1 640:grid-cols-2 768:grid-cols-1 800:grid-cols-2 1280:grid-cols-3 gap-2">
        {data.map((team) => (
          <Card key={team.id} as={Link} className="flex cursor-pointer p-2 gap-2" elevation={1} to={`/sst/${team.id}`}>
            <div className="flex items-center shrink">
              <Shield className="text-5xl" src={team.avatar} srcImageSize={IMAGE_SIZES.FIT_100_100} />
            </div>
            <div className="flex flex-col justify-start grow gap-1 overflow-hidden">
              <div className="font-bold truncate">{team.name}</div>
              <div className="flex justify-between items-end gap-2 text-xs">
                <div>
                  {team.nickname}
                  <div>
                    {team.relevant_tournaments.length === 0 && <span>Sin torneos</span>}
                    {team.relevant_tournaments.length === 1 && (
                      <div key={team.relevant_tournaments[0]?.id} className="flex gap-1 items-center">
                        <Trophy
                          className="text-lg"
                          src={team.relevant_tournaments[0]?.avatar}
                          srcImageSize={IMAGE_SIZES.FIT_45_45}
                        />
                        {team.relevant_tournaments[0]?.name}
                      </div>
                    )}
                    {team.relevant_tournaments.length > 1 && (
                      <Tooltip
                        tooltip={
                          <div className="flex flex-col gap-1 p-1">
                            {team.relevant_tournaments.slice(0, 4).map((tournament) => (
                              <div key={tournament.id} className="flex gap-1 items-center">
                                <Trophy
                                  className="text-lg"
                                  src={tournament.avatar}
                                  srcImageSize={IMAGE_SIZES.FIT_45_45}
                                />
                                {tournament.name}
                              </div>
                            ))}
                            {team.relevant_tournaments.length > 4 && (
                              <div className="flex gap-1 items-center">{team.relevant_tournaments.length - 4} más</div>
                            )}
                          </div>
                        }
                      >
                        <span>{team.relevant_tournaments.length} torneos</span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end text-sm">
                <Tooltip tooltip="Seleccionar este equipo">
                  <SimpleButton icon="plus" onClick={onSelectTeam} tertiary value={team.id}>
                    Seleccionar
                  </SimpleButton>
                </Tooltip>
              </div>
              <div className="flex justify-end gap-2 text-xs">
                {team.discipline?.name && (
                  <div className="px-2 rounded-md border border-current text-blue truncate">{team.discipline.name}</div>
                )}
                {team.gender?.name && (
                  <div className="px-2 rounded-md border border-current text-green truncate">{team.gender.name}</div>
                )}
                {team.category?.name && (
                  <div className="px-2 rounded-md border border-current text-orange truncate">{team.category.name}</div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
      {pagination && search.trim().length >= 3 && (
        <div className="text-sm flex flex-wrap  items-center justify-end gap-1">
          <span>
            Página {pagination.current_page} de {pagination.last_page}
          </span>
          {page > 1 && (
            <SimpleButton className="text-sm" onClick={() => setPage(page - 1)}>
              Anterior
            </SimpleButton>
          )}
          {page < pagination.last_page && <SimpleButton onClick={() => setPage(page + 1)}>Siguiente</SimpleButton>}
        </div>
      )}
      {!loading && allowCreation && pagination && search.trim().length >= 3 && (
        <div className="text-sm flex flex-wrap  items-center justify-end gap-1">
          Crear un equipo con el nombre buscado:{' '}
          <SimpleButton className="text-sm" icon="plug" onClick={onCreateTeam}>
            Crear
          </SimpleButton>
        </div>
      )}
    </div>
  )
}

export default SearchTeam
